import {
  Panel,
  PanelAction,
  PanelContent,
  PanelHeader,
  PanelIcon,
  PanelMeta,
  PanelTitle,
} from '@multaqa/ui/components/ui/panel'
import { cn } from '@multaqa/ui/lib/utils'
import type { ReactNode } from 'react'

/**
 * One label/value pair inside a record section: the label at the start, the
 * value beside it, a hairline between rows.
 *
 * R12: a read-only computed value is never a boxed field. These are label,
 * value, and a hairline between rows; nothing that looks like an input the
 * reader could type into.
 *
 * `mono` marks the values a SYSTEM generated — codes, dates, amounts, ids (R8).
 * Human language stays in the plain body style.
 */
export function RecordField({
  label,
  value,
  mono = false,
  tone,
  testId,
}: {
  label: string
  value: ReactNode
  mono?: boolean
  tone?: 'default' | 'danger' | 'warning'
  testId?: string
}) {
  return (
    <div
      className="border-border grid min-w-0 grid-cols-[minmax(7rem,0.4fr)_minmax(0,1fr)] items-baseline gap-2.5 border-b px-bleed py-2.5"
      data-testid={testId}
    >
      <span className="text-small text-muted-foreground min-w-0">{label}</span>
      <span
        className={cn(
          'text-body min-w-0 break-words',
          mono && 'text-identifier tabular-nums',
          tone === 'danger' && 'text-danger',
          tone === 'warning' && 'text-warning',
        )}
      >
        {value === null || value === undefined || value === '' ? (
          <span className="text-muted-foreground">—</span>
        ) : mono ? (
          <bdi dir="ltr">{value}</bdi>
        ) : (
          value
        )}
      </span>
    </div>
  )
}

/**
 * A panel of RecordFields, or nothing at all.
 *
 * **The `absent` prop is the whole point.** A field group the caller may not
 * see is omitted from the API payload entirely, and this section then does not
 * render — no heading, no empty box, no "you do not have permission" placeholder
 * advertising the existence of data they cannot have. The module rail follows
 * the same rule: "the nav is the permission set, not a menu with locked items".
 */
export function RecordSection({
  title,
  meta,
  action,
  description,
  icon,
  absent = false,
  stack = false,
  children,
  testId,
}: {
  title: string
  meta?: string
  action?: ReactNode
  description?: string
  icon?: ReactNode
  absent?: boolean
  /** One block of content (a schedule, a policy) instead of a two-column field grid. */
  stack?: boolean
  children: ReactNode
  testId?: string
}) {
  if (absent) return null

  const hasDetails = Boolean(description)

  return (
    <Panel data-testid={testId}>
      <PanelHeader className={hasDetails ? 'items-start' : undefined}>
        {icon ? <PanelIcon>{icon}</PanelIcon> : null}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <PanelTitle role="heading" aria-level={2}>
                {title}
              </PanelTitle>
              {meta ? <PanelMeta className="text-label tabular-nums">{meta}</PanelMeta> : null}
            </div>
            {description ? (
              <p className="text-small text-muted-foreground mt-1">{description}</p>
            ) : null}
          </div>
        </div>
        {action ? <PanelAction>{action}</PanelAction> : null}
      </PanelHeader>
      <PanelContent
        className={cn(
          'grid min-w-0 grid-cols-1',
          !stack &&
            'md:grid-cols-2 md:[&>div:nth-child(odd)]:border-e md:[&>div:nth-child(even)]:ps-[max(var(--bleed),1rem)]',
        )}
      >
        {children}
      </PanelContent>
    </Panel>
  )
}
